import {
  Box,
  Card,
  Text,
  CardHeader,
  CardBody,
  NameValueList,
  NameValuePair,
  Image,
  Button,
  TextInput,
  Notification,
} from "grommet";
import { getOrganizationAction } from "../../config/organizationActions";
import { useState, useEffect } from "react";
import PodExamCodesLayer from "./PodExamCodesLayer";
import { API, Storage } from "aws-amplify";
import { GetLoungeToken } from "../../graphql/queries";

const JOTFORM_BASE_URL = "https://monitoredu.jotform.com/";

const getJotformUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${JOTFORM_BASE_URL}${path}`;
};

const resolveImageUrl = async (path) => {
  if (!path) return "";
  if (path.startsWith("files/")) {
    const options = {
      level: "public",
      customPrefix: { public: "", protected: "", private: "" },
    };
    console.log("[PodTesterInfoCard] Resolving S3 image path:", {
      key: path,
      options,
    });
    try {
      const s3Url = await Storage.get(path, options);
      console.log("[PodTesterInfoCard] S3 image URL resolved:", {
        key: path,
        s3Url,
      });
      return s3Url;
    } catch (error) {
      console.error("[PodTesterInfoCard] Failed to resolve S3 image path", {
        key: path,
        error,
      });
      return "";
    }
  }
  console.log("[PodTesterInfoCard] Using Jotform image path:", path);
  return getJotformUrl(path);
};

export default function PodTesterInfoCard({
  podSession,
  tocicoId,
  localMeetLink,
  setLocalMeetLink,
  handleSaveMeetLink,
  meetLinkLoading,
  handleImageClick,
  setShowIntegrationLayer,
  handleVerifyIdentity,
  meetLoading,
  handleGenerateGoogleMeetLink,
  podExamCodes,
  loungeStream,
  userEmail,
}) {
  const [showExamCodesLayer, setShowExamCodesLayer] = useState(false);
  const [loungeToken, setLoungeToken] = useState(null);
  const [idPictureUrl, setIdPictureUrl] = useState("");
  const [secondIdPictureUrl, setSecondIdPictureUrl] = useState("");
  const [selfiePictureUrl, setSelfiePictureUrl] = useState("");
  const [showLoungeTokenNotification, setShowLoungeTokenNotification] =
    useState(false);

  useEffect(() => {
    async function getLoungeToken() {
      if (loungeStream !== null) {
        const token = await API.graphql({
          query: GetLoungeToken,
          variables: {
            action: "getLoungeToken",
            payload: JSON.stringify({
              roomName: loungeStream.roomName,
              proctorName: userEmail,
              testerName: podSession.testerName,
              testerOrganization: podSession.organizationName,
            }),
          },
        });
        console.log("token", token);
        setLoungeToken(JSON.parse(token.data.getLoungeToken).body);
      }
    }
    getLoungeToken();
  }, [loungeStream, userEmail, podSession.testerName]);

  useEffect(() => {
    async function setResolvedImageUrls() {
      const [resolvedIdPictureUrl, resolvedSecondIdPictureUrl, resolvedSelfie] =
        await Promise.all([
          resolveImageUrl(podSession.idPictureUrlPath),
          resolveImageUrl(podSession.secondIdPictureUrlPath),
          resolveImageUrl(podSession.selfiePictureUrlPath),
        ]);
      console.log("[PodTesterInfoCard] Final resolved image URLs:", {
        idPictureUrlPath: podSession.idPictureUrlPath,
        resolvedIdPictureUrl,
        secondIdPictureUrlPath: podSession.secondIdPictureUrlPath,
        resolvedSecondIdPictureUrl,
        selfiePictureUrlPath: podSession.selfiePictureUrlPath,
        resolvedSelfiePictureUrl: resolvedSelfie,
      });
      setIdPictureUrl(resolvedIdPictureUrl);
      setSecondIdPictureUrl(resolvedSecondIdPictureUrl);
      setSelfiePictureUrl(resolvedSelfie);
    }

    setResolvedImageUrls();
  }, [
    podSession.idPictureUrlPath,
    podSession.secondIdPictureUrlPath,
    podSession.selfiePictureUrlPath,
  ]);

  function handleCopyLoungeToken() {
    // get the domain of the current window
    const domain = window.location.hostname;
    if (domain.includes("localhost")) {
      const loungeTokenUrl = `http://localhost:5173/waitingroom?token=${loungeToken}&roomName=${loungeStream.roomName}`;
      navigator.clipboard.writeText(loungeTokenUrl);
    } else {
      const loungeTokenUrl = `https://meet.monitoredu.live/waitingroom?token=${loungeToken}&roomName=${loungeStream.roomName}`;
      navigator.clipboard.writeText(loungeTokenUrl);
    }

    setShowLoungeTokenNotification(true);
  }
  return (
    <Card fill height={{ min: "large" }}>
      {showLoungeTokenNotification && (
        <Notification
          status="normal"
          toast
          onClose={() => setShowLoungeTokenNotification(false)}
          title="Lounge Token Copied!"
          message="Lounge Token Copied To Clipboard, please send to the tester!"
        />
      )}
      {showExamCodesLayer && (
        <PodExamCodesLayer
          podExamCodes={podExamCodes}
          podSession={podSession}
          setShowExamCodesLayer={setShowExamCodesLayer}
          showExamCodesLayer={showExamCodesLayer}
        />
      )}
      <CardHeader
        pad="medium"
        border={{ side: "bottom", color: "gray", size: "xsmall" }}
      >
        <Text weight="bold" size="large">
          {podSession.testerName}
        </Text>
        <Text weight="bold" size="large">
          {podSession.organizationName}
        </Text>
      </CardHeader>
      <CardBody pad="medium">
        <Box direction="column" gap="small">
          <NameValueList>
            <NameValuePair name="Email">
              <Text>{podSession.testerEmail}</Text>
            </NameValuePair>
            <NameValuePair name="Phone">
              <Text>{podSession.testerPhone}</Text>
            </NameValuePair>
            {(podSession.fields ?? []).map((field) => (
              <NameValuePair name={field.key} key={field.key}>
                <Text>{field.value}</Text>
              </NameValuePair>
            ))}
            {podSession.verified_by && (
              <NameValuePair name="ID Verified By">
                <Text>{podSession.verified_by}</Text>
              </NameValuePair>
            )}
            <NameValuePair name="Google Meet Link">
              <Box direction="row" gap="small" align="center">
                <TextInput
                  value={localMeetLink}
                  onChange={(e) => setLocalMeetLink(e.target.value)}
                  placeholder="Enter Google Meet link"
                  size="small"
                />
                <Button
                  label="Save"
                  size="small"
                  primary
                  onClick={handleSaveMeetLink}
                  disabled={meetLinkLoading}
                />
                {meetLinkLoading && <Text size="small">Saving...</Text>}
              </Box>
            </NameValuePair>
          </NameValueList>
        </Box>
        <Box align="end" justify="start" margin={{ top: "small" }}>
          {podSession.organizationId === "abmdi" && (
            <Button
              label="Open ABMDI Integration"
              primary
              color="black"
              onClick={() => setShowIntegrationLayer(true)}
            />
          )}
          {podExamCodes.length > 0 && (
            <Button
              label="Open Exam Codes"
              primary
              color="black"
              onClick={() => setShowExamCodesLayer(true)}
            />
          )}
          {podSession.organizationId === "hpe" && (
            <Button
              label="Open HPE Integration"
              primary
              color="black"
              onClick={() => setShowIntegrationLayer(true)}
            />
          )}
          {podSession.organizationId === "tocico" && (
            <Button
              label="Open TOCICO Integration"
              primary
              color="black"
              title={tocicoId ? `Matched exam ID: ${tocicoId}` : undefined}
              onClick={() => setShowIntegrationLayer(true)}
            />
          )}
          {/*you loungeToken && (
            
            <Button
              label="Copy Lounge Link"
              primary
              color="black"
              onClick={handleCopyLoungeToken}
              disabled={true}

            />
            
            */}
        </Box>
        <Box
          direction="row"
          align="center"
          justify="center"
          gap="small"
          pad="small"
        >
          <Box>
            <Image
              src={idPictureUrl}
              fit="contain"
              style={{ cursor: "pointer" }}
              onError={(error) =>
                console.error("[PodTesterInfoCard] ID image failed to render", {
                  src: idPictureUrl,
                  error,
                })
              }
              onClick={() => handleImageClick(idPictureUrl, "ID Picture")}
            />
            <Text>ID Picture (Click to enlarge)</Text>
          </Box>
          {podSession.secondIdPictureUrlPath && (
            <Box>
              <Image
                src={secondIdPictureUrl}
                fit="contain"
                style={{ cursor: "pointer" }}
                onError={(error) =>
                  console.error(
                    "[PodTesterInfoCard] Second ID image failed to render",
                    {
                      src: secondIdPictureUrl,
                      error,
                    },
                  )
                }
                onClick={() =>
                  handleImageClick(secondIdPictureUrl, "Second ID Picture")
                }
              />
              <Text>Second ID Picture (Click to enlarge)</Text>
            </Box>
          )}
          <Box>
            <Image
              src={selfiePictureUrl}
              fit="contain"
              style={{ cursor: "pointer" }}
              onError={(error) =>
                console.error(
                  "[PodTesterInfoCard] Selfie image failed to render",
                  {
                    src: selfiePictureUrl,
                    error,
                  },
                )
              }
              onClick={() =>
                handleImageClick(selfiePictureUrl, "Selfie Picture")
              }
            />
            <Text>Selfie Picture (Click to enlarge)</Text>
          </Box>
        </Box>
        <Box gap="small">
          {(!podSession.verified_by || podSession.verified_by === "") && (
            <Button
              label="I have verified the ID and selfie"
              primary
              color="status-ok"
              onClick={handleVerifyIdentity}
            />
          )}
          {meetLoading && <Text>Loading...</Text>}
          {/* Dynamic action button based on organization configuration */}
          {!meetLoading &&
            (() => {
              const action = getOrganizationAction(podSession.organizationId);
              if (!action.showCondition(podSession)) return null;

              return (
                <Button
                  label={action.label}
                  primary
                  color="black"
                  onClick={() => {
                    if (action.action === "redirect") {
                      window.open(action.url, "_blank");
                    } else if (action.action === "generate") {
                      handleGenerateGoogleMeetLink();
                    }
                  }}
                  disabled={meetLoading}
                />
              );
            })()}
        </Box>
      </CardBody>
    </Card>
  );
}
